import React from 'react'
import PropTypes from 'prop-types'
import {
    Menu,
    MenuList,
    MenuOptionGroup,
    MenuItemOption,
    MenuButton,
    Text,
    Flex,
    Icon,
    useStyleConfig
} from '@chakra-ui/react'
import { ChevronDownIcon, Globe, GlobeDark, CheckMarkIcon } from '../icons'
import {FormattedMessage} from 'react-intl'

const CountrySelectorDropdown = ({
    countries,
    language,
    countryCode,
    handleLanguageChange,
    variant = 'header'
}) => {
    const styles = useStyleConfig('CountrySelector', {variant})

    const getFooterLabel = (locale) => {
        const [language, country] = locale.split('_')
        const localeTag = `${language}-${country}`
        const displayNames = new Intl.DisplayNames([localeTag], { type: 'region' })
        const countryName = displayNames.of(country)

        const currency = countries?.[locale]?.currency
        const currencySymbol = currency
            ? new Intl.NumberFormat(localeTag, {
                  style: 'currency',
                  currency
              }).format(0).replace(/[\d.,]/g, '').trim()
            : ''

        return currencySymbol
            ? `${countryName} - ${language.toUpperCase()} (${currencySymbol})`
            : `${countryName} - ${language.toUpperCase()}`
    }

    const GlobeIcon = variant === 'light' ? GlobeDark : Globe

    return (
        <Flex sx={{
                '[data-popper-placement="bottom-end"]': {inset: {base: 'unset', md: '-1px 0 auto auto !important', lg: '-2px 0 auto auto !important'}
                }}}>
            <Menu placement="bottom-end">
                {({isOpen}) => (
                    <>
                        <MenuButton {...styles.menuButton}>
                            <Flex {...styles.trigger}>
                                {variant === 'footer' ? (
                                    <>
                                        <Icon as={GlobeDark} {...styles.globeIcon} />
                                        <Text {...styles.footerLabel}>
                                            {getFooterLabel(language)}
                                        </Text>
                                        <Text {...styles.footerChange}>
                                            <FormattedMessage
                                                defaultMessage="Change Country"
                                                id="country_selector.change_country"
                                            />
                                        </Text>
                                    </>
                                ) : (
                                    <>
                                        <Icon as={GlobeIcon} {...styles.globeIcon} />
                                        <Text {...styles.headerCode}>
                                            {countryCode}
                                        </Text>
                                        <Icon
                                            as={ChevronDownIcon}
                                            transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                                            {...styles.chevronIcon}
                                        />
                                    </>
                                )}
                            </Flex>
                        </MenuButton>
                        <MenuList {...styles.menuList}>
                            <MenuOptionGroup
                                defaultValue={language}
                                value={language}
                                onChange={(e) => handleLanguageChange(e)}
                            >
                                {countries &&
                                    Object.entries(countries).map(([lang, {label}], index) => {
                                        const menuItemStyles =
                                            index === 0
                                                ? {...styles.menuItem, ...styles.borderTopNone}
                                                : styles.menuItem
                                        const isSelected = language === lang
                                        return (
                                            <MenuItemOption
                                                key={lang}
                                                value={lang}
                                                icon={null}
                                                iconSpacing={0}
                                                {...menuItemStyles}
                                            >
                                                <Flex {...styles.menuItemContent}>
                                                    <Text as="span" {...styles.menuItemLabel}>
                                                        {label}
                                                    </Text>
                                                    {isSelected ? (
                                                        <Icon as={CheckMarkIcon} {...styles.menuItemCheck} />
                                                    ) : null}
                                                </Flex>
                                            </MenuItemOption>
                                        )
                                    })
                                }
                            </MenuOptionGroup>
                        </MenuList>
                    </>
                )}
            </Menu>
        </Flex>
    )
}

CountrySelectorDropdown.propTypes = {
    countries: PropTypes.object,
    language: PropTypes.string,
    countryCode: PropTypes.string,
    handleLanguageChange: PropTypes.func
}

export default CountrySelectorDropdown
